package org.teddapp.project.core

/**
 * Ce que le noyau Rust rend à l'application. L'interface est étroite à dessein :
 * l'application n'exécute aucun calcul de domaine, elle écrit des faits et lit
 * trois choses, l'instantané, le fil des réactions et la recommandation.
 *
 * Tout passe en JSON pour éviter de dupliquer les types du noyau dans deux
 * langages et de les laisser diverger.
 */
interface TeddCoreBridge {
    /** Écrit un fait et renvoie l'enveloppe produite, en JSON. */
    fun appendEvent(userId: String, eventJson: String): String

    /** Applique au instantané les faits écrits depuis la dernière ouverture. */
    fun catchUp(): Long

    /** Instantané de projection : compétences, finances, cohérence. */
    fun snapshotJson(): String

    /** Réactions produites depuis [cursor], avec le curseur suivant. */
    fun reactionsSince(cursor: Long): CoreReactions

    /** Ce que l'agent recommande, pour l'état donné. */
    fun recommendation(timeOfDay: String, fatigue: String, focusStreak: Int, physicalReady: Boolean): CoreRecommendation

    /** Nombre d'événements dans le journal. */
    fun eventCount(): Long

    /**
     * Nombre de situations que l'agent a apprises. Zéro signifie qu'il récite son
     * heuristique de démarrage à froid au lieu d'apprendre.
     */
    fun agentKnownStates(): Long

    /** Jours d'attente par case de Leitner, de la première à la dernière. */
    fun leitnerIntervals(): List<Int>

    /** Version du noyau natif, à confronter à celle de l'application. */
    fun coreVersion(): String

    /** Enveloppes écrites après [after], en JSON. Sert au diagnostic, pas à la synchronisation. */
    fun eventsAfter(after: Long): List<String>

    /** Insère une enveloppe venue d'un autre appareil. */
    fun appendReplica(envelopeJson: String): String

    /**
     * Exécute un cycle de synchronisation du journal : push puis pull.
     *
     * Le noyau garde l'algorithme — curseur persisté, idempotence sur l'identifiant
     * du fait, ordre de rejeu — et n'emprunte à l'application que le transport.
     */
    suspend fun synchronize(transport: CoreJournalTransport): CoreSyncReport

    /** Libère la bibliothèque native. */
    fun close()
}

/** Une réaction du noyau, prête pour le fil d'Aujourd'hui. */
data class CoreReaction(val kind: String, val message: String, val offset: Long)

/** Réactions et curseur à conserver. */
data class CoreReactions(val reactions: List<CoreReaction>, val cursor: Long)

/** Recommandation de l'agent. */
data class CoreRecommendation(val action: String, val minutes: Int, val label: String)

/**
 * Transport du journal vers le broker central. Le noyau l'appelle, l'application
 * l'exécute : c'est elle qui détient la session avec le serveur.
 */
interface CoreJournalTransport {
    /**
     * Appelé avant chaque cycle. Un broker que l'appareil découvre y reçoit tout le
     * journal local : le curseur du noyau, laissé par un autre broker, croirait sinon
     * ces faits déjà publiés.
     */
    suspend fun prepare(core: TeddCoreBridge) {}

    /** Appelé après un cycle réussi, une fois le curseur du noyau enregistré. */
    suspend fun afterCycle() {}

    /**
     * Publie des enveloppes JSON et renvoie les enregistrements globaux obtenus,
     * chacun de la forme `{"global_offset":n,"envelope":{…}}`.
     *
     * Le broker ignore les enveloppes qu'il connaît déjà : la liste renvoyée peut
     * être plus courte que celle publiée.
     */
    suspend fun publish(envelopes: List<String>): List<String>

    /** Enregistrements globaux d'offset strictement supérieur à [after], par ordre croissant. */
    suspend fun fetchAfter(after: Long): List<String>
}

/** Bilan d'un cycle de synchronisation du journal. */
data class CoreSyncReport(
    val pushed: Int,
    val pulled: Int,
    val pushedLocalOffset: Long,
    val lastGlobalOffset: Long,
)

/**
 * Ouvre le noyau dans [dir] pour l'appareil [deviceId], ou renvoie null quand la
 * bibliothèque native n'est pas disponible sur cette plateforme. L'application
 * fonctionne alors exactement comme avant, en local.
 */
expect fun openTeddCore(dir: String, deviceId: String): TeddCoreBridge?
